import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BarChart3, Layers, Sparkles, Target,
  Compass, Users, Workflow, ShieldCheck, Check, Brain, TrendingUp,
  FileText, Map, Zap
} from "lucide-react";

const MODULES = [
  {
    id: "mvip",
    name: "MVIP",
    tagline: "Valuación Inteligente",
    icon: BarChart3,
    color: "from-[#CCD500] to-[#9DAB00]",
    description: "Matriz de Homologación Progresiva con IA. Genera valuaciones comerciales precisas con dictamen técnico, análisis fotográfico y PDF profesional firmado digitalmente.",
    features: [
      "Matriz de Homologación de 6 factores",
      "Dictamen IA con Claude Sonnet",
      "PDF profesional 4 páginas firmado",
      "Análisis fotográfico automatizado",
    ],
    metric: "5 valuaciones",
    metricLabel: "gratis al mes",
    mock: { type: "valuation", value: "$8,250,000", range: "$7.4M – $9.1M", trend: "+12% anual" },
  },
  {
    id: "terreno",
    name: "Terreno",
    tagline: "Potencial de Desarrollo",
    icon: Layers,
    color: "from-emerald-400 to-emerald-700",
    description: "Análisis de 4 escenarios de desarrollo (Casa, Pequeño Desarrollo, Edificio, Comercial) con ROI, costos notariales mexicanos y escenario recomendado.",
    features: [
      "4 escenarios automáticos",
      "Costos notariales ISAI/IVA/ISR",
      "ROI y utilidad neta por escenario",
      "Recomendación basada en datos",
    ],
    metric: "$99 MXN",
    metricLabel: "por reporte adicional",
    mock: { type: "scenarios", best: "Edificio Departamentos", roi: "34.2%" },
  },
  {
    id: "match",
    name: "Match",
    tagline: "Afinidad Psicográfica",
    icon: Sparkles,
    color: "from-rose-400 to-rose-700",
    description: "52 puntos de compatibilidad divididos en 4 olas: emocional (35%), técnica (25%), urbana (20%) y financiera (20%). Solo verás leads con 70%+.",
    features: [
      "52 puntos psicográficos",
      "4 pilares ponderados",
      "Umbral mínimo 70%",
      "Leads pre-calificados",
    ],
    metric: "94%",
    metricLabel: "match promedio top",
    mock: { type: "match", pillars: [{ n: "Emoción", v: 92 }, { n: "Técnico", v: 88 }, { n: "Urbano", v: 85 }, { n: "Financiero", v: 79 }] },
  },
  {
    id: "ric",
    name: "RIC",
    tagline: "Reporte de Inteligencia Comercial",
    icon: Target,
    color: "from-blue-400 to-blue-700",
    description: "Reporte ejecutivo del estado comercial de tu cartera: leads activos, propiedades en pipeline, conversión y proyección de cierres.",
    features: [
      "Dashboard en tiempo real",
      "Funnel de leads",
      "Proyección de cierres",
      "Export a PDF",
    ],
    metric: "12",
    metricLabel: "leads activos",
    mock: { type: "ric", leads: 12, deals: 4, conversion: "33%" },
  },
  {
    id: "gap",
    name: "Gap",
    tagline: "Brecha de Oportunidad",
    icon: Compass,
    color: "from-purple-400 to-purple-700",
    description: "Detecta la brecha entre el precio de listado y el precio justo de mercado. Identifica propiedades subvaluadas para inversión.",
    features: [
      "Comparables automáticos",
      "Brecha % vs mercado",
      "Alerta de oportunidad",
      "Ranking por potencial",
    ],
    metric: "-18%",
    metricLabel: "brecha promedio",
    mock: { type: "gap", market: "$10.2M", listed: "$8.4M", gap: "-17.6%" },
  },
  {
    id: "audiencias",
    name: "Audiencias",
    tagline: "Segmentación Hiper-dirigida",
    icon: Users,
    color: "from-orange-400 to-orange-700",
    description: "Matriz de audiencias por perfil psicográfico, NSE AMAI, edad y ciclo de vida. Lanza campañas a compradores con alta compatibilidad.",
    features: [
      "Matriz NSE × Pilar",
      "Filtros por ciclo de vida",
      "Tamaño de audiencia en vivo",
      "Export para Meta/Google Ads",
    ],
    metric: "234",
    metricLabel: "audiencias activas",
    mock: { type: "audiences", segments: ["DINKs A/B", "Familias C+", "Inversionistas B"] },
  },
  {
    id: "funnel",
    name: "Funnel",
    tagline: "Control de Embudo",
    icon: Workflow,
    color: "from-cyan-400 to-cyan-700",
    description: "Pipeline visual del ciclo de venta: Match → Visita → Negociación → Cierre. Drag & drop, recordatorios automáticos y métricas por etapa.",
    features: [
      "4 etapas configurables",
      "Drag & drop entre etapas",
      "Recordatorios automáticos",
      "Conversion rate por agente",
    ],
    metric: "33%",
    metricLabel: "tasa de cierre",
    mock: { type: "funnel", stages: [{ n: "Match", v: 47 }, { n: "Visita", v: 22 }, { n: "Negoc.", v: 9 }, { n: "Cierre", v: 4 }] },
  },
  {
    id: "boveda",
    name: "Bóveda",
    tagline: "Documentos Legales",
    icon: ShieldCheck,
    color: "from-amber-300 to-yellow-700",
    description: "Almacenamiento cifrado de contratos, identificaciones, escrituras y comprobantes. Acceso controlado por usuario y bitácora de auditoría.",
    features: [
      "Cifrado en reposo",
      "Bitácora de accesos",
      "Compartir con caducidad",
      "OCR automático",
    ],
    metric: "256-bit",
    metricLabel: "cifrado AES",
    mock: { type: "vault", docs: 18, shared: 4 },
  },
];

const ModuleMock = ({ mock, color }) => {
  if (mock.type === "valuation") {
    return (
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-widest text-slate-500">Valor Comercial Estimado</p>
        <p className="font-display text-3xl font-black text-white">{mock.value}</p>
        <p className="text-xs text-slate-400">Rango ±10%: {mock.range}</p>
        <div className="flex items-center gap-2 text-xs text-emerald-400 font-semibold">
          <TrendingUp className="w-3.5 h-3.5" /> {mock.trend}
        </div>
      </div>
    );
  }
  if (mock.type === "scenarios") {
    return (
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-widest text-slate-500">Escenario Recomendado</p>
        <p className="font-display text-lg font-bold text-white">{mock.best}</p>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
          <Zap className="w-3 h-3" /> ROI {mock.roi}
        </div>
      </div>
    );
  }
  if (mock.type === "match") {
    return (
      <div className="space-y-2">
        {mock.pillars.map((p) => (
          <div key={p.n}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{p.n}</span>
              <span className="text-white font-semibold">{p.v}%</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${color}`} style={{ width: `${p.v}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (mock.type === "ric") {
    return (
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-2 rounded-lg bg-white/5">
          <p className="font-display text-xl font-black text-white">{mock.leads}</p>
          <p className="text-[10px] text-slate-400 uppercase">Leads</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-white/5">
          <p className="font-display text-xl font-black text-white">{mock.deals}</p>
          <p className="text-[10px] text-slate-400 uppercase">Deals</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-[#CCD500]/10 border border-[#CCD500]/30">
          <p className="font-display text-xl font-black text-[#CCD500]">{mock.conversion}</p>
          <p className="text-[10px] text-slate-400 uppercase">Conv.</p>
        </div>
      </div>
    );
  }
  if (mock.type === "gap") {
    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Mercado</span>
          <span className="text-white font-semibold">{mock.market}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Listado</span>
          <span className="text-white font-semibold">{mock.listed}</span>
        </div>
        <div className="pt-2 border-t border-white/10 flex justify-between items-center">
          <span className="text-xs uppercase tracking-wider text-slate-500">Brecha</span>
          <span className="font-display text-2xl font-black text-emerald-400">{mock.gap}</span>
        </div>
      </div>
    );
  }
  if (mock.type === "audiences") {
    return (
      <div className="space-y-2">
        {mock.segments.map((s) => (
          <div key={s} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
            <span className="text-sm text-white">{s}</span>
            <span className="text-xs text-[#CCD500] font-semibold">activo</span>
          </div>
        ))}
      </div>
    );
  }
  if (mock.type === "funnel") {
    const max = mock.stages[0].v;
    return (
      <div className="space-y-2">
        {mock.stages.map((s) => (
          <div key={s.n} className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-14">{s.n}</span>
            <div className="flex-1 h-6 bg-white/5 rounded-md overflow-hidden">
              <div className={`h-full bg-gradient-to-r ${color} flex items-center justify-end pr-2`} style={{ width: `${(s.v / max) * 100}%` }}>
                <span className="text-[10px] font-bold text-black">{s.v}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (mock.type === "vault") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" />
          <div>
            <p className="font-display text-lg font-black text-white">{mock.docs}</p>
            <p className="text-[10px] text-slate-400 uppercase">Documentos</p>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-white/5 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <div>
            <p className="font-display text-lg font-black text-white">{mock.shared}</p>
            <p className="text-[10px] text-slate-400 uppercase">Compartidos</p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const DemoPage = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(MODULES[0].id);
  const current = MODULES.find((m) => m.id === selected);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="demo-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Volver al inicio</span>
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png"
            alt="Findperty"
            className="h-8 brightness-0 invert"
          />
          <Button
            onClick={() => navigate("/auth")}
            className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all rounded-full px-5"
            data-testid="demo-cta-btn"
          >
            Probar 7 días gratis
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 pb-12 px-6 lg:px-8 overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#CCD500]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <Breadcrumbs items={[{ label: "Demo Agente" }]} className="mb-8" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
            <Brain className="w-3.5 h-3.5" /> Agente Inteligente Findperty
          </span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6">
            8 módulos que transforman <br className="hidden md:block" />
            <span className="bg-gradient-to-r from-[#CCD500] to-[#E5FF00] bg-clip-text text-transparent">
              datos en cierres
            </span>.
          </h1>
          <p className="mt-7 text-lg text-slate-400 max-w-2xl mx-auto">
            Un arsenal completo de inteligencia comercial inmobiliaria. Valuación, terreno, matching psicográfico, RIC, gap, audiencias, funnel y bóveda legal — todo en una sola plataforma.
          </p>
        </div>
      </section>

      {/* Module Tabs */}
      <section className="px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
            <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
            {MODULES.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelected(m.id)}
                data-testid={`demo-tab-${m.id}`}
                className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                  selected === m.id
                    ? "bg-[#CCD500] text-black border-[#CCD500] shadow-[0_0_20px_rgba(204,213,0,0.3)]"
                    : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10 hover:text-white"
                }`}
              >
                <m.icon className="w-4 h-4" />
                {m.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Module Detail */}
      <section className="px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-12 gap-8"
            data-testid={`demo-detail-${current.id}`}
          >
            {/* Left: Info */}
            <div className="lg:col-span-7 rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-8 md:p-12">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${current.color} mb-6 shadow-lg`}>
                <current.icon className="w-8 h-8 text-black" />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#CCD500] mb-2">{current.tagline}</p>
              <h2 className="font-display text-4xl md:text-5xl font-black text-white tracking-tight">{current.name}</h2>
              <p className="mt-5 text-slate-400 text-lg leading-relaxed">{current.description}</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {current.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 text-[#CCD500] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-200">{f}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate("/auth")}
                className="mt-8 bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 rounded-full px-7 h-12"
                data-testid={`demo-module-cta-${current.id}`}
              >
                Probar {current.name}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Right: Mock Preview */}
            <div className="lg:col-span-5 space-y-4">
              <div className="rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Vista previa · {current.name}</span>
                </div>
                <ModuleMock mock={current.mock} color={current.color} />
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-[#CCD500]/10 to-transparent border border-[#CCD500]/20 p-6">
                <p className="text-xs uppercase tracking-widest text-[#CCD500] mb-2">{current.metricLabel}</p>
                <p className="font-display text-4xl font-black text-white">{current.metric}</p>
              </div>
            </div>
          </motion.div>

          {/* All Modules Grid */}
          <div className="mt-20">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-display text-2xl font-bold text-white">Los 8 módulos del Agente</h3>
              <span className="text-xs uppercase tracking-widest text-slate-500">Click para explorar</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {MODULES.map((m, i) => (
                <motion.button
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => {
                    setSelected(m.id);
                    window.scrollTo({ top: 400, behavior: "smooth" });
                  }}
                  data-testid={`demo-grid-${m.id}`}
                  className={`group text-left p-5 rounded-2xl border transition-all ${
                    selected === m.id
                      ? "bg-[#CCD500]/10 border-[#CCD500]/40"
                      : "bg-slate-900/40 border-white/10 hover:border-[#CCD500]/30 hover:-translate-y-1"
                  }`}
                >
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#CCD500]/60 to-transparent opacity-0 group-hover:opacity-100" />
                  <m.icon className="w-5 h-5 text-[#CCD500] mb-3" />
                  <p className="font-display text-base font-bold text-white">{m.name}</p>
                  <p className="text-xs text-slate-400 mt-1">{m.tagline}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-5xl font-black text-black tracking-tighter mb-4">
              Activa tu Agente Inteligente.
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-7">
              7 días de prueba gratis para Agentes y Propietarios. Sin tarjeta.
            </p>
            <Button
              onClick={() => navigate("/auth")}
              size="lg"
              className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base"
              data-testid="demo-final-cta"
            >
              Probar 7 días gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          © 2026 Findperty. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
